// ═══════════════════════════════════════════════════════════════════════════════
// Client channel — Play Store TWA vs installed PWA vs browser tab
// PWABuilder / Bubblewrap start URL should include ?client=play
// ═══════════════════════════════════════════════════════════════════════════════

import { isRunningAsPwa } from './pwaRuntime';

export type ClientChannel = 'play' | 'pwa' | 'web';

export const CLIENT_CHANNEL_HEADER = 'X-Client-Channel';

const SESSION_STORAGE_KEY = 'fdh-client-channel';
const QUERY_PARAM = 'client';
const PLAY_VALUE = 'play';

const readSessionPlayFlag = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === PLAY_VALUE;
  } catch {
    return false;
  }
};

const writeSessionPlayFlag = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, PLAY_VALUE);
  } catch {
    // ignore sessionStorage write failures
  }
};

const isAndroidAppReferrer = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.referrer.startsWith('android-app://');
};

/**
 * Capture `?client=play` from the URL into sessionStorage (per TWA window),
 * then strip the query param so shared links stay clean.
 * Uses sessionStorage (not localStorage) so Chrome tabs / home-screen PWA
 * on the same origin are not poisoned when storage is shared with TWA.
 */
export const captureClientChannelFromUrl = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  if (url.searchParams.get(QUERY_PARAM) !== PLAY_VALUE) {
    return;
  }

  writeSessionPlayFlag();
  url.searchParams.delete(QUERY_PARAM);
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
};

export const getClientChannel = (): ClientChannel => {
  if (typeof window === 'undefined') {
    return 'web';
  }

  if (readSessionPlayFlag() || (isRunningAsPwa() && isAndroidAppReferrer())) {
    return 'play';
  }

  if (isRunningAsPwa()) {
    return 'pwa';
  }

  return 'web';
};

/** Stripe checkout / portal / plan-change UI — web & installed PWA only. */
export const canUseStripeBillingUi = (): boolean => {
  const channel = getClientChannel();
  return channel === 'web' || channel === 'pwa';
};

export const isPlayStoreClient = (): boolean => getClientChannel() === 'play';
